import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListTagsResult, type SimilarTagsResult } from '@whinerp/client';
import {
  Button,
  EmptyState,
  Field,
  InlineAction,
  Num,
  Panel,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * Setup > Tags (F-033).
 *
 * The manager, and the part of it that earns its place is the near-duplicate
 * list. Case and spacing cannot make two tags - the database normalises and
 * the index is unique - so what is left is punctuation, plurals and
 * transpositions, and those are the ones that turn a vocabulary into noise.
 *
 * Each pair comes with the reason it was flagged, so somebody can disagree.
 * A merge offered with no explanation is a merge people accept wrongly.
 */
export function TagsPanel(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const errorMessage = useErrorMessage();
  const client = useQueryClient();
  const [label, setLabel] = useState('');
  const [merged, setMerged] = useState<string | null>(null);

  const tags = useQuery<ListTagsResult, ApiError>({
    queryKey: ['tags'],
    queryFn: () => api.listTags({}),
  });
  const similar = useQuery<SimilarTagsResult, ApiError>({
    queryKey: ['tags-similar'],
    queryFn: () => api.similarTags({}),
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['tags'] });
    void client.invalidateQueries({ queryKey: ['tags-similar'] });
  };

  const create = useMutation({
    mutationFn: () => api.createTag({ body: { label } }),
    onSuccess: () => {
      setLabel('');
      refresh();
    },
  });

  const merge = useMutation({
    mutationFn: (input: { keepId: string; mergeId: string }) =>
      api.mergeTags({ body: input }),
    onSuccess: (result) => {
      setMerged(
        t('tags.merged', {
          values: { count: formatCount(result.moved, format), label: result.kept.label },
        }),
      );
      refresh();
    },
  });

  const govern = useMutation({
    mutationFn: (governed: boolean) => api.setTagGovernance({ body: { governed } }),
    onSuccess: refresh,
  });

  // A skeleton means "still loading". Rendering one on error is a spinner that
  // never resolves.
  if (tags.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (tags.isError) return <></>;

  return (
    <Panel data-region="tags" id="tags">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('tags.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('tags.body')}</p>

        <div className="flex flex-wrap items-end gap-2">
          <Field
            label={t('tags.add')}
            value={label}
            data-tag-label
            onChange={(event) => setLabel(event.target.value)}
            className="min-w-[14rem] flex-1"
          />
          <Button
            data-tag-create
            intent="primary"
            disabled={label.trim() === '' || create.isPending}
            onClick={() => create.mutate()}
          >
            {t('tags.add')}
          </Button>
        </div>
        {create.isError ? (
          <p role="alert" data-tag-error className="text-meta text-danger">
            {errorMessage(create.error)}
          </p>
        ) : null}

        <InlineAction data-tag-governance onClick={() => govern.mutate(!tags.data.governed)}>
          {tags.data.governed ? t('tags.governed') : t('tags.ungoverned')}
        </InlineAction>

        {/*
          The near-duplicates, with the reason each pair was flagged.

          A merge offered with no explanation is a merge people accept wrongly,
          and two tags that look alike are sometimes two different things.
        */}
        <SectionLabel>{t('tags.similar')}</SectionLabel>
        {similar.isPending ? (
          <Skeleton className="h-block-xs w-full" />
        ) : similar.isError ? (
          <></>
        ) : similar.data.pairs.length === 0 ? (
          <p data-tag-no-similar className="text-meta text-ink-meta">
            {t('tags.noSimilar')}
          </p>
        ) : (
          <ul className="flex flex-col gap-2" data-tag-similar>
            {similar.data.pairs.map((pair) => (
              <li
                key={`${pair.a.tagId}:${pair.b.tagId}`}
                data-tag-pair
                className="flex flex-wrap items-center gap-2 rounded-chip bg-sunken px-3 py-2"
              >
                <span className="text-body text-ink">{pair.a.label}</span>
                <span className="text-meta text-ink-meta">·</span>
                <span className="text-body text-ink">{pair.b.label}</span>
                <span data-tag-reason className="text-meta text-ink-meta">
                  {pair.reason}
                </span>
                <InlineAction
                  data-tag-merge={pair.b.tagId}
                  onClick={() => merge.mutate({ keepId: pair.a.tagId, mergeId: pair.b.tagId })}
                >
                  {t('tags.merge', { values: { label: pair.a.label } })}
                </InlineAction>
              </li>
            ))}
          </ul>
        )}
        {merged ? (
          <p data-tag-merged role="status" className="text-meta text-ink-meta">
            {merged}
          </p>
        ) : null}

        {tags.data.tags.length === 0 ? (
          <EmptyState
            data-tag-empty
            illustration="Tag"
            title={t('tags.title')}
            body={t('tags.empty')}
            primary={{ label: t('tags.add'), icon: 'Plus', href: '/setup#tags' }}
          />
        ) : (
          <ul className="flex flex-wrap gap-2" data-tag-list>
            {tags.data.tags.map((tag) => (
              <li
                key={tag.tagId}
                data-tag={tag.tagId}
                className="flex items-center gap-2 rounded-chip bg-sunken px-3 py-1"
              >
                <span className="text-body text-ink">{tag.label}</span>
                {/* The count, because a tag on nothing is the one to merge or
                    remove and a list without it hides which. */}
                <Num data-tag-uses className="text-meta text-ink-meta">
                  {t('tags.uses', { values: { count: formatCount(tag.uses, format) } })}
                </Num>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}
